/**
 * Shares ONE `useHumanCallWorkspace` instance between the Review layout's Call Script
 * column and its Output pane (BV-1575).
 *
 * Both panes drive the same working set. Calling the hook twice would give them two
 * independent working outputs and — worse — two debounced persistence layers racing
 * writes to the same call, so the instance is owned here and read by both.
 *
 * The provider renders a Fragment, not a wrapper element: it sits directly inside the
 * Review columns' flex row, where an extra DOM node would become a flex item and break
 * the layout. It is mounted ONLY in the Call Script branch, preserving the property that
 * the workspace does not run for cases and calls that never show the script.
 */
import React, { createContext, useContext } from 'react';

import {
  type HumanCallWorkspace,
  useHumanCallWorkspace,
  type UseHumanCallWorkspaceArgs,
} from './useHumanCallWorkspace';

const HumanCallWorkspaceContext = createContext<HumanCallWorkspace | null>(null);

export const HumanCallWorkspaceProvider: React.FC<
  UseHumanCallWorkspaceArgs & { children: React.ReactNode }
> = ({ children, ...args }) => {
  const workspace = useHumanCallWorkspace(args);
  return (
    <HumanCallWorkspaceContext.Provider value={workspace}>
      {children}
    </HumanCallWorkspaceContext.Provider>
  );
};

/**
 * The shared workspace, or null outside the Call Script layout. Consumers render their
 * own fallback rather than throwing: the Output pane is also used by every other call,
 * where no provider is mounted.
 */
export function useHumanCallWorkspaceContext(): HumanCallWorkspace | null {
  return useContext(HumanCallWorkspaceContext);
}
